import {
  ApolloClient,
  InMemoryCache,
  ApolloLink,
  Observable,
  from,
  HttpLink,
  Operation
} from '@apollo/client';
import { onError } from '@apollo/client/link/error';
import { setContext } from '@apollo/client/link/context';
import { RetryLink } from '@apollo/client/link/retry';
import { store } from 'src/app/store';
import getDashboardDateTime from 'src/utils/getDashboardDateTime';
import { projectIds } from 'src/constants/projectIds';

const connections: { [key: string]: any } = {};
const operationNameMap: Map<string, AbortController> = new Map();

const projectIdPemprovJateng = 'f8f9ea6a-18aa-4b7b-8e19-7f4a2498bf1e';
const projectIdTransportUmum = 'dbfaae30-4da4-4e6d-a1c9-718df02e06ee';

const isUsingClusteringEndpoint = (endpoint: string, projectId: string) => {
  return (
    endpoint === 'clustering' ||
    projectId === projectIdPemprovJateng ||
    projectId === projectIdTransportUmum
  );
};

const getTimeValue = (date?: Date | string | number | null) => {
  if (!date) return null;
  const time = date instanceof Date ? date.getTime() : new Date(date).getTime();
  return Number.isNaN(time) ? null : time;
};

// const isMutationOperation = (operation: Operation) => {
//   return operation.query.definitions.some(
//     (definition) =>
//       definition.kind === 'OperationDefinition' &&
//       definition.operation === 'mutation'
//   );
// };

const isUsingNoDuplicateEndpoint = (operation: Operation) => {
  const { variables, operationName } = operation;
  const isEcommerceOperation = window.location.href.includes('ecommerce');
  // const isNotificationOperation = operationName.includes('notification_');
  const isInstagramIntegration = operationName.includes(
    'InstagramPerformance_'
  );
  const project = store.getState().users.project;
  const isNoLimitDemo = projectIds.nolimit_demo === project?.id;
  const isAsuransiAstra = projectIds.asuransi_astra === project?.id;
  const isFrutella = projectIds.frutella === project?.id;
  const timeframe = store.getState().analyticArguments.dateRange;

  if (
    isInstagramIntegration &&
    (isAsuransiAstra || isFrutella || isNoLimitDemo)
  )
    return true;

  if (!project?.createdByV6) return false;

  const startDate = getDashboardDateTime(
    variables?.start || variables?.startDate || timeframe?.start
  );

  const cutOffTime = isEcommerceOperation
    ? getTimeValue(project?.cutOffV6Ecomerce)
    : getTimeValue(project?.cutOffV6Socmed);

  // will use no duplicate endpoint if createdByV6 is true
  // and start date is later than cut off time

  return startDate !== null && cutOffTime !== null && startDate >= cutOffTime;
};

const projectIdLink = setContext(() => {
  const projectId = localStorage.getItem('project');
  return { projectId: projectId ?? '' };
});

const userIdLink = setContext(() => {
  const userId = store.getState().users.userProfile.data?.userId;
  return { userId: userId ?? '' };
});

const noDuplicateDirectionalLink = ApolloLink.split(
  (operation) => isUsingNoDuplicateEndpoint(operation),
  new HttpLink({
    uri: process.env.REACT_APP_NO_DUPLICATE_API_URL,
    credentials: 'include'
  }),
  new HttpLink({ uri: process.env.REACT_APP_API_URL, credentials: 'include' })
);

// conditional endpoint based on contexts
const httpDirectionalLink = ApolloLink.split(
  // conditional
  (operation) =>
    isUsingClusteringEndpoint(
      operation.getContext().endpoint,
      operation.getContext().projectId
    ),
  // if condition true
  new HttpLink({
    uri: process.env.REACT_APP_CLUSTERING_API_URL,
    credentials: 'include'
  }),
  // if condition false
  noDuplicateDirectionalLink
);

// const cancelRequestLink = new ApolloLink(
//   (operation, forward) =>
//     new Observable((observer) => {
//       const context = operation.getContext();
//       const connectionHandle = forward(operation).subscribe({
//         next: (...arg) => observer.next(...arg),
//         error: (...arg) => {
//           cleanUp();
//           observer.error(...arg);
//         },
//         complete: (...arg) => {
//           cleanUp();
//           observer.complete(...arg);
//         }
//       });

//       const cleanUp = () => {
//         connectionHandle?.unsubscribe();
//         delete connections[context.requestTrackerId];
//       };

//       if (context.requestTrackerId) {
//         const controller = new AbortController();
//         controller.signal.onabort = cleanUp;
//         operation.setContext({
//           ...context,
//           fetchOptions: {
//             signal: controller.signal,
//             ...context?.fetchOptions,
//             queryDeduplication: false
//           }
//         });

//         if (connections[context.requestTrackerId]) {
//           console.log('Abort!');
//           connections[context.requestTrackerId]?.abort();
//         }

//         connections[context.requestTrackerId] = controller;
//       }

//       return connectionHandle;
//     })
// );

// Helper: Check if operation should use auto-abort by name
interface OperationFilterConfig {
  include?: string[];
  exclude?: string[];
  caseSensitive?: boolean;
}

const shouldAutoAbortByOperationName = (
  operationName: string,
  config: OperationFilterConfig = {}
): boolean => {
  if (!operationName) return false;

  const { include = [], exclude = [], caseSensitive = false } = config;

  const targetName = caseSensitive
    ? operationName
    : operationName.toLowerCase();

  // Check exclude list first (higher priority)
  const shouldExclude = exclude.some((pattern) => {
    const targetPattern = caseSensitive ? pattern : pattern.toLowerCase();
    return targetName.includes(targetPattern);
  });

  if (shouldExclude) return false;

  // Check include list
  const shouldInclude = include.some((pattern) => {
    const targetPattern = caseSensitive ? pattern : pattern.toLowerCase();
    return targetName.includes(targetPattern);
  });

  return shouldInclude;
};

// Contoh penggunaan:

// 1. Default (hanya conversation)
// shouldAutoAbortByOperationName('getUserConversation'); // true

// // 2. Multiple include patterns
// shouldAutoAbortByOperationName('getAnalytics', {
//   include: ['conversation', 'content', 'analytic']
// }); // true

// 3. Dengan exclude
// shouldAutoAbortByOperationName('getConversationHistory', {
//   include: ['conversation'],
//   exclude: ['history']
// }); // false

// 4. Case sensitive
// shouldAutoAbortByOperationName('GetConversation', {
//   include: ['conversation'],
//   caseSensitive: true
// }); // false (karena 'C' besar)

// 5. Kombinasi lengkap
// shouldAutoAbortByOperationName('getUserContentData', {
//   include: ['content', 'conversation', 'user'],
//   exclude: ['admin', 'test'],
//   caseSensitive: false
// }); // true

const cancelRequestLink = new ApolloLink(
  (operation, forward) =>
    new Observable((observer) => {
      const context = operation.getContext();
      const operationName = operation.operationName;
      const requestTrackerId = context.requestTrackerId;

      let controller: AbortController;
      let useOperationNameTracking = false;

      //STRATEGY 1: requestTrackerId provided (manual tracking)
      if (requestTrackerId) {
        if (connections[requestTrackerId]) {
          console.log(`Aborting: ${requestTrackerId}`);
          connections[requestTrackerId].abort();
          delete connections[requestTrackerId];
        }

        controller = new AbortController();
        connections[requestTrackerId] = controller;
      }

      //STRATEGY 2: No requestTrackerId, but operation name includes "conversation"
      else if (
        shouldAutoAbortByOperationName(operationName, {
          include: ['conversation_'],
          exclude: ['conversation_getTopTalkWithPaginationV2'],
          caseSensitive: false
        })
      ) {
        useOperationNameTracking = true;

        // Abort previous request with same operation name
        if (operationNameMap.has(operationName)) {
          console.log(`[OperationName] Aborting: ${operationName}`);
          const previousController = operationNameMap.get(operationName);
          if (previousController) {
            previousController.abort();
          }
          operationNameMap.delete(operationName);
        }

        controller = new AbortController();
        operationNameMap.set(operationName, controller);
      }

      //STRATEGY 3: No tracking (let it run)
      else {
        controller = new AbortController();
      }

      // Set abort signal to operation
      operation.setContext({
        ...context,
        fetchOptions: {
          signal: controller.signal,
          ...context?.fetchOptions
        }
      });

      const cleanUp = () => {
        connectionHandle?.unsubscribe();

        // Cleanup from appropriate map
        if (requestTrackerId) {
          delete connections[requestTrackerId];
        }
        if (useOperationNameTracking && operationName) {
          operationNameMap.delete(operationName);
        }
      };

      const connectionHandle = forward(operation).subscribe({
        next: (...arg) => {
          observer.next(...arg);
        },
        error: (...arg) => {
          cleanUp();
          // Don't propagate AbortError
          if (arg[0]?.name !== 'AbortError') {
            observer.error(...arg);
          } else {
            console.log(
              `✅ Request aborted: ${operationName || requestTrackerId}`
            );
          }
        },
        complete: (...arg) => {
          cleanUp();
          observer.complete(...arg);
        }
      });

      // Cleanup when aborted from outside
      controller.signal.addEventListener('abort', () => {
        connectionHandle.unsubscribe();
      });

      return connectionHandle;
    })
);

const customRequestHeaderLink = new ApolloLink((operation, forward) => {
  const { operationName, variables } = operation;
  const projectId = operation.getContext().projectId;
  const userId = operation.getContext().userId;
  const startDate = variables?.start;
  const endDate = variables?.end;

  operation.setContext(({ headers = {} }) => ({
    headers: {
      ...headers,
      'x-operation-name': operationName || '',
      'x-project-id': projectId || '',
      'x-user-id': userId || '',
      'x-start-date': startDate || '',
      'x-end-date': endDate || ''
    }
  }));
  return forward(operation);
});

const errorLink = onError(({ graphQLErrors, networkError, operation }) => {
  if (graphQLErrors) {
    graphQLErrors.forEach((error, index) => {
      console.group(
        `GraphQL error [${index + 1}] in operation ${operation.operationName}`
      );
      console.error('Message:', error.message);

      if (error.locations) {
        console.error('Locations:', JSON.stringify(error.locations, null, 2));
      }

      console.error('Path:', error.path);
      console.error('Extensions:', error.extensions);

      console.error('Variables:', JSON.stringify(operation.variables, null, 2));

      console.error('Full error object:', error);
      console.groupEnd();
    });
  }

  if (networkError) {
    console.group('Network error');
    console.error(networkError);
    console.groupEnd();
  }
});

const defaultOptions: any = {
  watchQuery: {
    fetchPolicy: 'network-only',
    errorPolicy: 'all',
    notifyOnNetworkStatusChange: true
  },
  query: {
    fetchPolicy: 'network-only',
    errorPolicy: 'all',
    notifyOnNetworkStatusChange: true
  },
  mutate: {
    errorPolicy: 'all'
  }
};

const authLink = setContext((_, { headers }) => {
  const token = localStorage.getItem('token');
  return {
    headers: {
      ...headers,
      'X-JWT-Token': token ? token : ''
    }
  };
});

const retryLink = new RetryLink({
  attempts: (count, operation, error) => {
    if (count >= 5) return false;
    switch (error.message) {
      // catch error for chrome
      case 'Failed to fetch':
        return true;
      // catch error for firefox
      case 'NetworkError when attempting to fetch resource.':
        return true;
      // catch error for safari
      case 'Load failed':
        return true;
      default:
        return false;
    }
  },
  delay: (count, operation, error) => {
    console.log('Retrying ' + operation.operationName);
    return count * 1000 * Math.random();
  }
});

const client = new ApolloClient({
  link: from([
    errorLink,
    projectIdLink,
    userIdLink,
    customRequestHeaderLink,
    retryLink,
    authLink,
    projectIdLink,
    cancelRequestLink,
    httpDirectionalLink
  ]),
  cache: new InMemoryCache({
    addTypename: false
  }),
  defaultOptions,
  connectToDevTools: true
});

export default client;
